import { apiClient } from '@/lib/api-client';
import type { TranscriptItem, XrayItem } from '@/lib/outbox';
import type { AuditEvent, Entry, EntryPatch, NewEntry, Transcript, Visit, Xray } from '@/types/api';

export const entriesApi = {
  async list(patientId: string) {
    const { data } = await apiClient.get<Entry[]>(`/patients/${patientId}/entries`);
    return data;
  },
  /** `visitId`: add to that visit (e.g. a confirmed one) instead of the open visit of the day. */
  async create(
    patientId: string,
    entries: NewEntry[],
    dentistId: string | null,
    visitId?: string | null
  ) {
    const { data } = await apiClient.post<Entry[]>(`/patients/${patientId}/entries`, {
      dentistId: dentistId ?? undefined,
      visitId: visitId ?? undefined,
      entries,
    });
    return data;
  },
  async reject(id: string, reason?: string) {
    const { data } = await apiClient.post<Entry>(`/entries/${id}/reject`, { reason });
    return data;
  },
  async correct(id: string, patch: EntryPatch) {
    const { data } = await apiClient.patch<Entry>(`/entries/${id}`, patch);
    return data;
  },
};

export const visitsApi = {
  /** Entering the total price confirms the visit. */
  async confirm(id: string, priceRsd: number) {
    const { data } = await apiClient.post<Visit>(`/visits/${id}/confirm`, { priceRsd });
    return data;
  },
  async setPrice(id: string, priceRsd: number) {
    const { data } = await apiClient.patch<Visit>(`/visits/${id}`, { priceRsd });
    return data;
  },
  /** "Fiskal": marks the visit for the e-karton export. */
  async setFiscal(id: string, fiscal: boolean) {
    const { data } = await apiClient.patch<Visit>(`/visits/${id}`, { fiscal });
    return data;
  },
  /** Who changed what in the visit, oldest first. */
  async log(id: string) {
    const { data } = await apiClient.get<AuditEvent[]>(`/visits/${id}/log`);
    return data;
  },
};

export const transcriptsApi = {
  async list(patientId: string) {
    const { data } = await apiClient.get<Transcript[]>(`/patients/${patientId}/transcripts`);
    return data;
  },
  /** The whole text again, under the dictation's clientId (idempotent). */
  async save(
    patientId: string,
    clientId: string,
    body: { lang: string; text: string; dentistId: string | null; recordedAt: string }
  ) {
    const { data } = await apiClient.put<Transcript>(
      `/patients/${patientId}/transcripts/${clientId}`,
      { ...body, dentistId: body.dentistId ?? undefined }
    );
    return data;
  },
  /** A dictation queued on the device. */
  saveItem(item: TranscriptItem) {
    return transcriptsApi.save(item.patientId, item.clientId, {
      lang: item.lang,
      text: item.text,
      dentistId: item.dentistId ?? null,
      recordedAt: item.recordedAt,
    });
  },
  async edit(id: string, text: string) {
    const { data } = await apiClient.patch<Transcript>(`/transcripts/${id}`, { text });
    return data;
  },
};

export const xraysApi = {
  async list(patientId: string) {
    const { data } = await apiClient.get<Xray[]>(`/patients/${patientId}/xrays`);
    return data;
  },
  /** The JPEG itself as the body, under the image's clientId (idempotent). */
  async upload(item: XrayItem) {
    const { data } = await apiClient.put<Xray>(
      `/patients/${item.patientId}/xrays/${item.clientId}`,
      item.blob,
      {
        headers: { 'Content-Type': 'image/jpeg' },
        params: {
          takenAt: item.takenAt,
          width: item.width,
          height: item.height,
          teeth: item.teeth.join(','),
          visitId: item.visitId ?? undefined,
          dentistId: item.dentistId ?? undefined,
        },
        // A few hundred KB over a slow clinic connection.
        timeout: 60000,
      }
    );
    return data;
  },
  /** The image as a blob (the request needs the auth header, so no plain <img src>). */
  async image(id: string) {
    const { data } = await apiClient.get<Blob>(`/xrays/${id}/image`, {
      responseType: 'blob',
      timeout: 60000,
    });
    return data;
  },
  async remove(id: string) {
    await apiClient.delete(`/xrays/${id}`);
  },
};
